import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { fetchExecutiveDashboard } from '@/store/slices/dashboardSlice';
import { fetchPlanningPeriods } from '@/store/slices/planningPeriodsSlice';
import { fetchScenarios } from '@/store/slices/scenariosSlice';
import {
  DollarSign,
  Target,
  Layers,
  GitBranch,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Search,
  Bell,
} from 'lucide-react';
import { cn } from '@/utils/cn';

const ExecutiveDashboardV2Page: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { executive } = useSelector((state: RootState) => state.dashboard);
  const { periods: planningPeriods } = useSelector((state: RootState) => state.planningPeriods);
  const { scenarios } = useSelector((state: RootState) => state.scenarios);

  const [selectedPlanningPeriod, setSelectedPlanningPeriod] = useState<string>('');
  const [selectedScenario, setSelectedScenario] = useState<string>('');

  useEffect(() => {
    dispatch(fetchPlanningPeriods());
  }, [dispatch]);

  useEffect(() => {
    if (selectedPlanningPeriod) {
      dispatch(fetchScenarios({ planningPeriodId: selectedPlanningPeriod }));
    }
  }, [dispatch, selectedPlanningPeriod]);

  useEffect(() => {
    if (planningPeriods.length > 0 && !selectedPlanningPeriod) {
      const activePeriod = planningPeriods.find((p: any) => p.isActive);
      setSelectedPlanningPeriod(activePeriod?.id || planningPeriods[0]?.id || '');
    }
  }, [planningPeriods, selectedPlanningPeriod]);

  useEffect(() => {
    if (scenarios.length > 0 && !selectedScenario) {
      const baselineScenario = scenarios.find((s: any) => s.isBaseline);
      setSelectedScenario(baselineScenario?.id || scenarios[0]?.id || '');
    }
  }, [scenarios, selectedScenario]);

  useEffect(() => {
    if (selectedPlanningPeriod) {
      dispatch(fetchExecutiveDashboard({
        planningPeriodId: selectedPlanningPeriod,
        scenarioId: selectedScenario || undefined,
      }));
    }
  }, [dispatch, selectedPlanningPeriod, selectedScenario]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value / 1000000) + 'M';
  };

  const formatTeamWeeks = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const kpis = executive.kpis || {
    totalInvestment: { dollars: 0, teamWeeks: 0, change: 0 },
    q1Readiness: { percentage: 0, readyFeatures: 0, totalFeatures: 0, status: 'on_track' as const },
    activeEpics: { total: 0, onTrack: 0, atRisk: 0, offTrack: 0 },
    dependenciesAtRisk: { total: 0, critical: 0, high: 0 },
  };

  const categoryCompliance = executive.categoryCompliance || {
    business: { actual: 0, target: 75, status: 'compliant' as const },
    tech: { actual: 0, target: 15, status: 'compliant' as const },
    support: { actual: 0, target: 10, status: 'compliant' as const },
  };

  const quarterlyReadiness = executive.quarterlyReadiness || [];

  const investmentByIS = executive.investmentByIS || [];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      {/* Top Navigation */}
      <nav className="bg-[var(--surface)]/80 backdrop-blur-xl border-b border-[var(--border)] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center">
                <Layers className="w-5 h-5 text-[var(--text)]" />
              </div>
              <span className="text-lg font-semibold tracking-tight">Portfolio Manager</span>
            </div>
            <div className="flex items-center gap-1">
              <button className="px-4 py-2 text-sm bg-[var(--surface-muted)] text-[var(--text)] rounded-lg flex items-center gap-1">
                Dashboard <ChevronDown className="w-3 h-3" />
              </button>
              <button className="px-4 py-2 text-sm text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-muted)] rounded-lg flex items-center gap-1">
                Planning <ChevronDown className="w-3 h-3" />
              </button>
              <button className="px-4 py-2 text-sm text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-muted)] rounded-lg flex items-center gap-1">
                Reports <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                type="text"
                placeholder="Search EPICs, Features..."
                className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-lg pl-10 pr-4 py-2 text-sm w-64 focus:outline-none focus:border-cyan-500"
              />
            </div>
            <button className="relative p-2 hover:bg-[var(--surface-muted)] rounded-lg" aria-label="Notifications">
              <Bell className="w-5 h-5 text-[var(--muted)]" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-cyan-400 rounded-full"></span>
            </button>
            <div className="flex items-center gap-3 pl-4 border-l border-[var(--border)]">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-sm font-medium">
                JD
              </div>
              <span className="text-sm text-[var(--text)]">John Doe</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-1 text-[var(--text)]">Executive Dashboard</h1>
            <p className="text-[var(--text)]">Portfolio investment and delivery overview</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-cyan-500"
              value={selectedPlanningPeriod}
              onChange={(e) => setSelectedPlanningPeriod(e.target.value)}
            >
              {planningPeriods.map((period: any) => (
                <option key={period.id} value={period.id}>
                  {period.name} {period.isActive ? '(Active)' : ''}
                </option>
              ))}
            </select>
            <button className="px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-medium rounded-lg text-sm">
              Export Report
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {/* Total Investment */}
          <div className="gradient-card rounded-2xl p-6 border border-[var(--border)]/50">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-cyan-500/10 rounded-xl">
                <DollarSign className="w-6 h-6 text-accent-blue" />
              </div>
              <span className="flex items-center gap-1 text-emerald-400 text-sm font-medium">
                <TrendingUp className="w-4 h-4" /> +{kpis.totalInvestment.change}%
              </span>
            </div>
            <div className="text-3xl font-bold mb-1 text-[var(--text)]">
              {formatCurrency(kpis.totalInvestment.dollars)}
            </div>
            <div className="text-[var(--text)] text-sm font-medium">Total Investment</div>
            <div className="text-[var(--text)] text-xs mt-2">
              {formatTeamWeeks(kpis.totalInvestment.teamWeeks)} Team Weeks
            </div>
          </div>

          {/* Q1 Readiness */}
          <div className="gradient-card rounded-2xl p-6 border border-[var(--border)]/50">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-emerald-500/10 rounded-xl">
                <Target className="w-6 h-6 text-emerald-400" />
              </div>
              <span className={cn(
                "px-2 py-1 text-xs font-medium rounded-full",
                kpis.q1Readiness.status === 'on_track' && "bg-emerald-500/20 text-emerald-400",
                kpis.q1Readiness.status === 'at_risk' && "bg-amber-500/20 text-amber-400",
                kpis.q1Readiness.status === 'off_track' && "bg-status-error/20 text-status-error"
              )}>
                {kpis.q1Readiness.status === 'on_track' ? 'On Track' :
                 kpis.q1Readiness.status === 'at_risk' ? 'At Risk' : 'Off Track'}
              </span>
            </div>
            <div className="text-3xl font-bold mb-1 text-[var(--text)]">{kpis.q1Readiness.percentage}%</div>
            <div className="text-[var(--text)] text-sm font-medium">Q1 Readiness</div>
            <div className="text-[var(--text)] text-xs mt-2">
              {kpis.q1Readiness.readyFeatures} of {kpis.q1Readiness.totalFeatures} Features Ready
            </div>
          </div>

          {/* Active EPICs */}
          <div className="gradient-card rounded-2xl p-6 border border-[var(--border)]/50">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-violet-500/10 rounded-xl">
                <Layers className="w-6 h-6 text-violet-400" />
              </div>
            </div>
            <div className="text-3xl font-bold mb-1 text-[var(--text)]">{kpis.activeEpics.total}</div>
            <div className="text-[var(--text)] text-sm font-medium">Active EPICs</div>
            <div className="flex items-center gap-4 mt-2 text-xs font-medium">
              <span className="text-emerald-400">{kpis.activeEpics.onTrack} On Track</span>
              <span className="text-amber-400">{kpis.activeEpics.atRisk} At Risk</span>
              <span className="text-status-error">{kpis.activeEpics.offTrack} Off Track</span>
            </div>
          </div>

          {/* Dependencies */}
          <div className="gradient-card rounded-2xl p-6 border border-[var(--border)]/50">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-amber-500/10 rounded-xl">
                <GitBranch className="w-6 h-6 text-amber-400" />
              </div>
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full">
                Attention
              </span>
            </div>
            <div className="text-3xl font-bold mb-1 text-[var(--text)]">{kpis.dependenciesAtRisk.total}</div>
            <div className="text-[var(--text)] text-sm font-medium">Dependencies at Risk</div>
            <div className="text-[var(--text)] text-xs mt-2">
              {kpis.dependenciesAtRisk.critical} Critical, {kpis.dependenciesAtRisk.high} High
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          {/* Category Compliance */}
          <div className="bg-[var(--surface)] rounded-2xl p-6 border border-[var(--border)]">
            <h3 className="text-lg font-bold mb-6 text-[var(--text)]">Category Compliance</h3>
            <div className="space-y-5">
              {/* Business */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[var(--text)] font-medium">Business</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[var(--text)]">{categoryCompliance.business.actual}%</span>
                    <span className="text-xs text-[var(--text)]">/ {categoryCompliance.business.target}%</span>
                    {categoryCompliance.business.status === 'compliant' && (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    )}
                  </div>
                </div>
                <div className="h-2 bg-[var(--surface-muted)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                    style={{ width: `${categoryCompliance.business.actual}%` }}
                  ></div>
                </div>
              </div>

              {/* Tech */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[var(--text)] font-medium">Tech</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[var(--text)]">{categoryCompliance.tech.actual}%</span>
                    <span className="text-xs text-[var(--text)]">/ {categoryCompliance.tech.target}%</span>
                    {categoryCompliance.tech.status === 'compliant' && (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    )}
                  </div>
                </div>
                <div className="h-2 bg-[var(--surface-muted)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                    style={{ width: `${categoryCompliance.tech.actual}%` }}
                  ></div>
                </div>
              </div>

              {/* Support */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[var(--text)] font-medium">Support</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[var(--text)]">{categoryCompliance.support.actual}%</span>
                    <span className="text-xs text-[var(--text)]">/ {categoryCompliance.support.target}%</span>
                    {categoryCompliance.support.status === 'compliant' && (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    )}
                  </div>
                </div>
                <div className="h-2 bg-[var(--surface-muted)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                    style={{ width: `${categoryCompliance.support.actual}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Quarterly Readiness */}
          <div className="bg-[var(--surface)] rounded-2xl p-6 border border-[var(--border)]">
            <h3 className="text-lg font-bold mb-6 text-[var(--text)]">Quarterly Readiness</h3>
            <div className="space-y-4">
              {quarterlyReadiness.length > 0 ? (
                quarterlyReadiness.map((q, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="text-sm font-bold text-[var(--text)] w-8">Q{q.quarter}</span>
                    <div className="flex-1 h-8 bg-[var(--surface-muted)] rounded-lg overflow-hidden flex">
                      {q.readyPercentage > 0 && (
                        <div
                          className="h-full bg-emerald-500 flex items-center justify-center text-xs font-bold text-[var(--text)]"
                          style={{ width: `${q.readyPercentage}%` }}
                        >
                          {q.readyPercentage}%
                        </div>
                      )}
                      {q.inRefinementPercentage > 0 && (
                        <div className="h-full bg-amber-500" style={{ width: `${q.inRefinementPercentage}%` }}></div>
                      )}
                      {q.notReadyPercentage > 0 && (
                        <div className="h-full bg-slate-600" style={{ width: `${q.notReadyPercentage}%` }}></div>
                      )}
                    </div>
                    <span className="text-xs text-[var(--text)] font-medium w-16">{q.totalFeatures} feat</span>
                  </div>
                ))
              ) : (
                <>
                  {[1, 2, 3, 4].map((q) => (
                    <div key={q} className="flex items-center gap-4">
                      <span className="text-sm font-bold text-[var(--text)] w-8">Q{q}</span>
                      <div className="flex-1 h-8 bg-[var(--surface-muted)] rounded-lg overflow-hidden flex">
                        <div className="h-full bg-emerald-500 flex items-center justify-center text-xs font-bold text-[var(--text)]" style={{ width: '0%' }}>
                        </div>
                      </div>
                      <span className="text-xs text-[var(--text)] font-medium w-16">0 feat</span>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="flex items-center gap-6 mt-6 pt-4 border-t border-[var(--border)]">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-emerald-500"></div>
                <span className="text-xs text-[var(--text)]">Ready</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-amber-500"></div>
                <span className="text-xs text-[var(--text)]">In Refinement</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-slate-600"></div>
                <span className="text-xs text-[var(--text)]">Not Ready</span>
              </div>
            </div>
          </div>

          {/* Dependencies at Risk */}
          <div className="bg-[var(--surface)] rounded-2xl p-6 border border-[var(--border)]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-[var(--text)]">Dependencies at Risk</h3>
              <button className="text-accent-blue text-sm hover:underline">View All</button>
            </div>
            <div className="space-y-3">
              {executive.dependenciesAtRisk && executive.dependenciesAtRisk.length > 0 ? (
                executive.dependenciesAtRisk.slice(0, 3).map((dep: any, idx: number) => (
                  <div key={idx} className="p-4 bg-[var(--surface-muted)] rounded-xl border border-[var(--border)]/50">
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-semibold text-sm text-[var(--text)]">{dep.name}</span>
                      <span className={cn(
                        "px-2 py-0.5 rounded text-xs font-medium",
                        dep.severity === 'critical' && "bg-status-error/20 text-status-error",
                        dep.severity === 'high' && "bg-amber-500/20 text-amber-400"
                      )}>
                        {dep.daysOverdue ? `${dep.daysOverdue} days` : 'Overdue'}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--muted)]">{dep.description || 'Dependency details'}</div>
                  </div>
                ))
              ) : (
                <div className="text-center text-[var(--text)] text-sm py-8">
                  No dependencies at risk
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-2 gap-6">
          {/* Investment by IS */}
          <div className="bg-[var(--surface)] rounded-2xl p-6 border border-[var(--border)]">
            <h3 className="text-lg font-bold mb-6 text-[var(--text)]">Investment by Investible Structure</h3>
            {investmentByIS && investmentByIS.length > 0 ? (
              <div className="space-y-3">
                {investmentByIS.map((is: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ background: `hsl(${210 + idx * 20}, 70%, ${50 - idx * 5}%)` }}
                    ></div>
                    <span className="flex-1 text-sm text-[var(--text)] font-medium">{is.name}</span>
                    <span className="text-sm font-bold text-[var(--text)]">{formatCurrency(is.dollars)}</span>
                    <span className="text-xs text-[var(--text)] w-16">{formatTeamWeeks(is.teamWeeks)} TWs</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-[var(--text)] text-sm py-8">
                No investment data available
              </div>
            )}
          </div>

          {/* EPIC Delivery Trend */}
          <div className="bg-[var(--surface)] rounded-2xl p-6 border border-[var(--border)]">
            <h3 className="text-lg font-bold mb-6 text-[var(--text)]">EPIC Delivery Trend</h3>
            {executive.epicDeliveryTrend && executive.epicDeliveryTrend.length > 0 ? (
              <div className="h-48 flex items-end justify-between gap-4 px-4">
                {executive.epicDeliveryTrend.map((trend: any, idx: number) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex flex-col gap-0.5" style={{ height: '180px' }}>
                      {trend.offTrack > 0 && (
                        <div
                          className="bg-status-error rounded-t"
                          style={{ height: `${(trend.offTrack / trend.total) * 100}%` }}
                        ></div>
                      )}
                      {trend.atRisk > 0 && (
                        <div
                          className="bg-amber-500"
                          style={{ height: `${(trend.atRisk / trend.total) * 100}%` }}
                        ></div>
                      )}
                      {trend.onTrack > 0 && (
                        <div
                          className="bg-emerald-500 rounded-b flex-1"
                          style={{ height: `${(trend.onTrack / trend.total) * 100}%` }}
                        ></div>
                      )}
                    </div>
                    <span className="text-xs text-[var(--text)] font-medium">{trend.month}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-[var(--text)] text-sm py-8">
                No delivery trend data available
              </div>
            )}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-[var(--border)]">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-emerald-500"></div>
                <span className="text-xs text-[var(--text)]">On Track</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-amber-500"></div>
                <span className="text-xs text-[var(--text)]">At Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-status-error"></div>
                <span className="text-xs text-[var(--text)]">Off Track</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExecutiveDashboardV2Page;
